'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import useEmblaCarousel from 'embla-carousel-react';

// Função para remover estilos inline do HTML
const stripInlineStyles = (html: string): string => {
    if (!html) return '';
    return html.replace(/\s*style="[^"]*"/gi, '')
        .replace(/\s*style='[^']*'/gi, '');
};

interface Event {
    id: string;
    title?: string;
    duration?: string;
    schedule?: string;
    description: string;
    slug?: string;
    image?: string;
}

interface EventsCarouselProps {
    title?: string | React.ReactNode;
    subtitle?: string;
    buttonText?: string;
    events: Event[];
    className?: string;
    onEventClick?: (eventId: string) => void;
}

const CalendarIcon = () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.25 0V1.5H9.75V0H11.25V1.5H14.25C14.6642 1.5 15 1.83579 15 2.25V14.25C15 14.6642 14.6642 15 14.25 15H0.75C0.33579 15 0 14.6642 0 14.25V2.25C0 1.83579 0.33579 1.5 0.75 1.5H3.75V0H5.25ZM13.5 7.5H1.5V13.5H13.5V7.5ZM4.5 9V10.5H3V9H4.5ZM8.25 9V10.5H6.75V9H8.25ZM12 9V10.5H10.5V9H12ZM3.75 3H1.5V6H13.5V3H11.25V4.5H9.75V3H5.25V4.5H3.75V3Z" fill="#F7941D" />
    </svg>
);

const EventCard: React.FC<{ event: Event; onExpand: () => void; onHoverChange?: (isHovered: boolean) => void }> = ({
    event,
    onExpand,
    onHoverChange
}) => {
    const router = useRouter();
    const [imageError, setImageError] = useState(false);

    const cleanDescription = useMemo(
        () => stripInlineStyles(event.description),
        [event.description]
    );

    // Get image URL - handle both string and object formats
    const imageUrl = useMemo(() => {
        if (!event.image) return '/event.png';

        // If it's a string, use it directly
        if (typeof event.image === 'string') {
            return event.image || '/event.png';
        }

        // If it's an object with url property
        if (typeof event.image === 'object' && 'url' in event.image) {
            return (event.image as any).url || '/event.png';
        }

        return '/event.png';
    }, [event.image]);

    const handleClick = () => {
        if (event.slug) {
            router.push(`/noticias-eventos/eventos/${event.slug}`);
        } else {
            onExpand();
        }
    };

    return (
        <article
            className="w-full bg-white rounded-sm shadow-[0px_8.542858123779297px_20.26080894470215px_0px_rgba(139,141,142,0.15)] overflow-hidden flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group cursor-pointer"
            onClick={handleClick}
            onMouseEnter={() => onHoverChange?.(true)}
            onMouseLeave={() => onHoverChange?.(false)}
        >
            {/* Image Section */}
            <div className="w-full h-48 sm:h-52 md:h-40 relative overflow-hidden bg-gray-200">
                <Image
                    src={!imageError ? imageUrl : '/event.png'}
                    alt={event.description || 'Evento'}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-110"
                    sizes="(max-width: 768px) 85vw, 320px"
                    onError={() => setImageError(true)}
                    priority={false}
                />
            </div>

            {/* Content Section */}
            <div className="px-4 py-4 flex flex-col gap-2.5 flex-grow">
                {/* Date */}
                <div className="flex items-center gap-2">
                    <CalendarIcon />
                    <span className="text-[#F7941D] text-xs font-medium font-poppins uppercase">
                        {event.duration}
                    </span>
                </div>

                {/* Description */}
                <div className="text-[#252122] text-sm font-semibold font-nunito leading-snug line-clamp-3 flex-grow">
                    {cleanDescription}
                </div>

                {/* More Details Link */}
                <span
                    className="text-[#F15A29] text-sm font-normal font-poppins hover:underline transition-all duration-200 text-left mt-auto"
                >
                    + detalhes
                </span>
            </div>
        </article>
    );
};

export const EventsCarousel: React.FC<EventsCarouselProps> = ({

    events,
    className = '',
    onEventClick
}) => {

    const [currentIndex, setCurrentIndex] = useState(0);
    const itemsPerPage = 4;
    const totalPages = Math.ceil(events.length / itemsPerPage);

    // Embla Carousel para mobile com drag
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: 'start',
        skipSnaps: false,
        dragFree: true,
        containScroll: 'trimSnaps'
    });

    const [selectedMobileIndex, setSelectedMobileIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedMobileIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        setScrollSnaps(emblaApi.scrollSnapList());
        emblaApi.on('select', onSelect);
        onSelect();
    }, [emblaApi, onSelect]);

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    const handleExpand = (eventId: string) => {
        if (onEventClick) {
            onEventClick(eventId);
        }
    };

    const visibleEvents = events.slice(
        currentIndex * itemsPerPage,
        (currentIndex + 1) * itemsPerPage
    );

    return (
        <section className={`w-full bg-white bo py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 ${className}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Title Section */}


                {/* Desktop: Grid com paginação */}
                <div className="hidden md:block">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-8">
                        {visibleEvents.map((event) => (
                            <EventCard
                                key={event.id}
                                event={event}
                                onExpand={() => handleExpand(event.id)}
                            />
                        ))}
                    </div>

                    {/* Pagination Dots */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-3 mt-12">
                            {Array.from({ length: totalPages }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`rounded-full transition-all duration-300 ${index === currentIndex
                                            ? 'w-3 h-3 bg-[#F15A29]'
                                            : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    aria-label={`Ir para página ${index + 1}`}
                                    aria-current={index === currentIndex ? 'page' : undefined}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Mobile: Carousel com drag scroll */}
                <div className="md:hidden">
                    <div className="overflow-hidden" ref={emblaRef}>
                        <div className="flex gap-4">
                            {events.map((event) => (
                                <div key={event.id} className="flex-[0_0_85%] min-w-0 sm:flex-[0_0_48%]">
                                    <EventCard
                                        event={event}
                                        onExpand={() => handleExpand(event.id)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mobile Pagination Dots */}
                    {scrollSnaps.length > 1 && (
                        <div className="flex justify-center gap-2 mt-6">
                            {scrollSnaps.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => emblaApi?.scrollTo(index)}
                                    className={`rounded-full transition-all duration-300 ${index === selectedMobileIndex
                                            ? 'w-3 h-3 bg-[#F15A29]'
                                            : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    aria-label={`Ir para slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default EventsCarousel;